/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

/* eslint-env browser */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, VStack, SimpleGrid, Flex, Center, Code, Stack } from 'chakra-v3';
import { Text, Button } from '../components/Index';
import { TaskCard, type TaskCardProps } from '../components/TaskCard';
import UserHeader from '../components/headers/UserHeader';
import projectDesignA from '../assets/design/projects/project_a.svg';
import projectDesignB from '../assets/design/projects/project_b.svg';
import projectDesignC from '../assets/design/projects/project_c.svg';
import { WorkerRecord } from '@karya/core';
import { useGetAvatarsWithTask, useSyncForAvatar } from '../service/api/box/Hooks';
import { getActiveWorker } from '../service/db/Worker';
import { CircularProgress } from '@chakra-ui/icons';

const projectDesignAssets = [projectDesignA, projectDesignB, projectDesignC] as const;

/** Props for {@link RenderTaskCards} component */
type RenderTaskCardProps = { allTasks: Array<TaskCardProps> };

/**
 * Renders project cards in appropriate layout based on count
 */
const RenderTaskCards: React.FC<RenderTaskCardProps> = ({ allTasks }) => {
  if (allTasks.length === 0) return null;

  if (allTasks.length === 1) {
    return (
      <Flex justifyContent="center" alignItems="flex-start" w="100%">
        <TaskCard {...allTasks[0]} />
      </Flex>
    );
  }

  if (allTasks.length === 2) {
    return (
      <Flex justifyContent="center" alignItems="flex-start" gap={{ base: '32px', md: '60px' }} flexWrap="wrap" w="100%">
        {allTasks.map((props) => (
          <TaskCard key={props.task.id} {...props} />
        ))}
      </Flex>
    );
  }

  return (
    <SimpleGrid
      columns={3}
      columnGap={{ base: '32px', md: '60px' }}
      rowGap={{ base: '32px', md: '60px' }}
      justifyItems="center"
      alignItems="flex-start"
    >
      {allTasks.map((props) => (
        <TaskCard key={props.task.id} {...props} />
      ))}
    </SimpleGrid>
  );
};

/**
 * Projects page component
 * Displays user's active projects in a grid layout
 * Shows demo project when no active projects are available
 * Fetches user name and projects from API on mount
 */
const DashboardPage: React.FC = () => {
  const [activeWorker, setActiveWorker] = useState<WorkerRecord | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const { tasks, avatars, syncAvatarTasks, isLoading } = useGetAvatarsWithTask(activeWorker?.id);
  const syncForAvatar = useSyncForAvatar();

  const initialSyncDone = useRef(false);

  // Get tasks and initiate sync
  useEffect(() => {
    getActiveWorker()
      .unwrapResult()
      .toTuple()
      .then(([worker, error]) => {
        if (error) {
          console.error('Error retrieving active worker:', error.prettyPrint());
          setError(error);
          return;
        }
        setActiveWorker(worker);
      })
      .catch((err) => {
        setError(err);
        console.error('Error fetching active worker:', err, "This shouldn't happen.");
      });
  }, []);

  const syncAvatarsWithNoAssignments = useCallback(async () => {
    await Promise.allSettled(
      avatars.map(async (avatar) => {
        await syncForAvatar({ avatar, getNewAssignmentsIfNoneExist: true });
      }),
    );
  }, [avatars, syncForAvatar]);

  useEffect(() => {
    if (activeWorker) {
      void syncAvatarTasks().catch((err) => {
        console.error('Error syncing avatar tasks:', err);
      });
    }
  }, [activeWorker, syncAvatarTasks]);

  useEffect(() => {
    if (initialSyncDone.current || avatars.length === 0) {
      return;
    }

    initialSyncDone.current = true;

    syncAvatarsWithNoAssignments().catch((err) => {
      console.error('Error syncing avatars with no assignments:', err);
    });
  }, [avatars.length, syncAvatarsWithNoAssignments]);

  const userName = activeWorker?.profile?.data?.full_name ?? activeWorker?.profile?.data?.first_name ?? 'User';
  const taskCount = tasks?.length ?? 0;
  const hasActiveTasks = taskCount > 0;

  if (error) {
    return (
      <Center height="100vh" bg="bg.primary" color="text.primary">
        <Stack alignItems="center" textAlign="center" gap="16px" px="16px">
          <Text variant="heroHeading">Something went wrong. Please try refreshing the page to continue. </Text>
          <Code fontSize="xl" padding={5}>
            {error.message}
          </Code>
        </Stack>
      </Center>
    );
  }

  if (!activeWorker || isLoading) {
    return (
      <Center height="100vh" bg="bg.primary" color="text.primary">
        <Flex gap="16px" alignItems="center">
          <CircularProgress isIndeterminate />
          <Text variant="heroHeading">Loading your projects...</Text>
        </Flex>
      </Center>
    );
  }

  const allTasks: TaskCardProps[] = tasks
    .map((task, index) => ({
      task,
      index,
      designSrc: projectDesignAssets[index % projectDesignAssets.length],
      href: `/task/${task.id}`, // Redirect to the current assignment,
    }))
    // Sort tasks by creation date (descending order first)
    .sort((a, b) => new Date(a.task.created_at).getTime() - new Date(b.task.created_at).getTime());

  return (
    <Box minH="100vh" bg="bg.primary" color="text.primary">
      <UserHeader />
      {/* Main Content */}
      <Box px={{ base: '16px', md: '48px', lg: '240px' }} py={{ base: '40px', md: '80px', lg: '140px' }}>
        <VStack gap="24px" alignItems="center" w="100%">
          {/* Welcome Message */}
          <VStack gap="0" alignItems="center" textAlign="center">
            <Text variant="heroHeading">Welcome, {userName}!</Text>
            {hasActiveTasks ? (
              <Text variant="heroSubheading" mt="8px">
                You have {taskCount} active {taskCount === 1 ? 'task' : 'tasks'}
              </Text>
            ) : (
              <Stack gap="8px" alignItems="center" textAlign="center" mt="8px">
                <Text variant="heroEmptyState" mt="8px" maxW="824px">
                  We&apos;ll notify you when a task is available. You can explore demo projects.
                </Text>
                <Button variant="cta" onClick={() => void syncAvatarTasks()}>
                  Refresh Tasks
                </Button>
              </Stack>
            )}
          </VStack>

          {hasActiveTasks && (
            <Box w="100%" maxW="1200px" mt={{ base: '32px', md: '48px' }}>
              <RenderTaskCards allTasks={allTasks} />
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default DashboardPage;
